import React, { useRef, useState } from 'react'
import useQuery from '@/hooks/useQuery'

// One GET per typeahead. Only the last call in a 300 ms window fires, a reply
// to anything but the latest request is dropped (slow networks hand them back
// out of order), and cancel() drops the pending timer and any reply still in
// flight. `now` skips the wait: the on-focus default list shouldn't lag.
//
// A plain setTimeout in a ref, not throttle-debounce: its v2 cancel() marks
// the wrapper cancelled for good, so the first cancel would have silenced
// every later keystroke. (The old debounce(300, getRequest(...)) built a fresh
// wrapper per keystroke and never deferred anything.) getRequest is read
// through a ref because useQuery rebuilds it every render.
export default function useTypeahead () {
  const { getRequest } = useQuery()
  const [loading, setLoading] = useState(false)
  const latest = useRef(0)
  const request = useRef()
  request.current = getRequest

  const fire = (uri, params, callback) => {
    const id = ++latest.current
    request.current(uri, params, (err, data) => {
      if (id !== latest.current) return
      setLoading(false)
      callback(err, data)
    })
  }
  const timer = useRef()

  const get = (uri, params, callback, { now = false } = {}) => {
    clearTimeout(timer.current)
    setLoading(true)
    if (now) {
      fire(uri, params, callback)
    } else {
      timer.current = setTimeout(() => fire(uri, params, callback), 300)
    }
  }

  const cancel = () => {
    clearTimeout(timer.current)
    latest.current++
    setLoading(false)
  }

  return { get, cancel, loading }
}

// For Autosuggest's renderInputComponent. The suggestion list only renders
// once there are suggestions, so the first keystrokes would otherwise show
// nothing at all; a spinner in the input covers that. right-8 clears the
// native clear button a type=search input grows once it has text.
export const spinnerInput = (loading) => (inputProps) => (
  <div className='relative inline-block'>
    <input {...inputProps} />
    { loading && <svg className='absolute right-8 top-1/2 -mt-2 h-4 w-4 animate-spin text-gray-400' viewBox='0 0 24 24' fill='none' aria-label='Searching'>
      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
      <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z' />
    </svg> }
  </div>
)
